import EditIcon from "@mui/icons-material/Edit";
import GetAppIcon from "@mui/icons-material/GetApp";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { Box, CircularProgress, Tab, Tabs, Tooltip } from "@mui/material";
import IconButton from "@mui/material/IconButton";
import Moment from "moment";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { CSVDownload } from "react-csv";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import {
  getCityWhitelabelMappings,
  getWhiteLabel,
} from "../../../actions/cities";
import {
  clearRewardProgram,
  getRewardClaims,
  getRewardPrograms,
  getRewardRules,
} from "../../../actions/rewards";
import { getUser } from "../../../apis/user/user-apis";
import RewardProgramModal from "../../../components/modal/RewardProgramModal";
import Tag from "../../../components/Tags";
import useCustomTable from "../../../components/table/CustomTable";
import { TAG_COLOR } from "../../../constants/const";
import { useShowToast } from "../../../hooks";
import useFleetsByPaymentConfig from "../../../hooks/useFleetsByPaymentConfig";

const RewardsContainer = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [modalVisible, setModalVisible] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [rulesByProgramId, setRulesByProgramId] = useState({});
  const [startDownload, setStartDownload] = useState(false);
  const [usersMap, setUsersMap] = useState(new Map());
  const [usersLoading, setUsersLoading] = useState(false);

  const showToast = useShowToast();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const {
    fleetsByPaymentConfig,
    fleetCurrencies,
    loading: fleetsLoading,
    getFleetName,
  } = useFleetsByPaymentConfig();

  const rewardPrograms = useSelector((state) => state.rewards.rewardPrograms);
  const rewardProgramsSuccess = useSelector(
    (state) => state.rewards.rewardProgramsSuccess
  );
  const createSuccess = useSelector(
    (state) => state.rewards.createRewardProgramSuccess
  );
  const updateSuccess = useSelector(
    (state) => state.rewards.updateRewardProgramSuccess
  );
  const rewardClaims = useSelector((state) => state.rewards.rewardClaims);
  const rewardClaimsSuccess = useSelector(
    (state) => state.rewards.rewardClaimsSuccess
  );
  const rewardRules = useSelector((state) => state.rewards.rewardRules);
  const rewardRulesSuccess = useSelector(
    (state) => state.rewards.rewardRulesSuccess
  );

  useEffect(() => {
    dispatch(getWhiteLabel());
    dispatch(getCityWhitelabelMappings());
    dispatch(getRewardRules({ page: 0, size: 1000 }));
  }, [dispatch]);

  useEffect(() => {
    if (!rewardPrograms?.content?.length) {
      setRulesByProgramId({});
      return;
    }

    const programIds = rewardPrograms.content.map((p) => p.id);
    dispatch(
      getRewardRules({
        filter: {
          reward_program_ids: programIds,
        },
      })
    );
  }, [rewardPrograms, dispatch]);

  useEffect(() => {
    if (!rewardRules?.content) {
      setRulesByProgramId({});
      return;
    }

    const rulesMap = {};
    rewardRules.content.forEach((rule) => {
      rulesMap[rule.rewardProgramId] = rule;
    });
    setRulesByProgramId(rulesMap);
  }, [rewardRules]);

  const getAmountDisplay = (programId, paymentConfigId) => {
    const rule = rulesByProgramId[programId];
    if (!rule?.rewardConfig?.fixed_amount_minor) return "-";
    const currency = fleetCurrencies[paymentConfigId];
    if (!currency)
      return `${(rule.rewardConfig.fixed_amount_minor / 100).toFixed(2)} (no currency)`;
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: currency,
    }).format(rule.rewardConfig.fixed_amount_minor / 100);
  };

  const getPriorityDisplay = (programId) => {
    const rule = rulesByProgramId[programId];
    if (rule?.priority === undefined) return "-";
    return rule.priority;
  };

  const getRuleName = (ruleId) => {
    if (!rewardRules || !rewardRules.content) return `Rule ${ruleId}`;
    const rule = rewardRules.content.find((r) => r.id === ruleId);
    return rule ? rule.name : `Rule ${ruleId}`;
  };

  const formatClaimAmount = (amountMinor, currency) => {
    if (!currency) return `${(amountMinor / 100).toFixed(2)} (no currency)`;
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: currency,
    }).format(amountMinor / 100);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "COMPLETED":
        return TAG_COLOR.GREEN;
      case "PENDING":
        return TAG_COLOR.ORANGE;
      case "FAILED":
        return TAG_COLOR.RED;
      case "REVERSED":
      case "EXPIRED":
        return TAG_COLOR.GREY;
      default:
        return TAG_COLOR.GREY;
    }
  };

  const truncateUuid = (uuid) => {
    if (!uuid) return "-";
    return `${uuid.substring(0, 8)}...`;
  };

  const extractLegacyUserId = (claim) => {
    if (claim.legacyUserId) return claim.legacyUserId;
    if (claim.triggerEventId?.startsWith("registration_")) {
      return claim.triggerEventId.replace("registration_", "");
    }
    return null;
  };

  const allClaimUserIds = useMemo(() => {
    if (!rewardClaims?.content) return [];
    const ids = new Set();
    rewardClaims.content.forEach((claim) => {
      const legacyId = extractLegacyUserId(claim);
      if (legacyId) ids.add(legacyId);
    });
    return Array.from(ids);
  }, [rewardClaims]);

  useEffect(() => {
    const fetchUsers = async () => {
      const idsToFetch = allClaimUserIds.filter(
        (id) => !usersMap.has(String(id))
      );

      if (idsToFetch.length === 0) return;

      setUsersLoading(true);
      const newUsersMap = new Map(usersMap);

      const BATCH_SIZE = 50;
      for (let i = 0; i < idsToFetch.length; i += BATCH_SIZE) {
        const batch = idsToFetch.slice(i, i + BATCH_SIZE);
        await Promise.all(
          batch.map(async (id) => {
            try {
              const user = await getUser(id);
              newUsersMap.set(String(id), user);
            } catch (e) {
              newUsersMap.set(String(id), null);
            }
          })
        );
      }

      setUsersMap(newUsersMap);
      setUsersLoading(false);
    };

    if (allClaimUserIds.length > 0) {
      fetchUsers();
    }
  }, [allClaimUserIds]);

  const getUserDisplay = (legacyId) => {
    if (!legacyId) return null;
    const user = usersMap.get(String(legacyId));
    if (user) {
      return user.fullName || user.email || user.phone || `User ${legacyId}`;
    }
    return `User ${legacyId}`;
  };

  const getCityIdForClaim = (claim) => {
    if (!rewardRules?.content || !claim.rewardRuleId) return null;
    const rule = rewardRules.content.find((r) => r.id === claim.rewardRuleId);
    if (!rule?.rewardProgramId || !rewardPrograms?.content) return null;
    const program = rewardPrograms.content.find(
      (p) => p.id === rule.rewardProgramId
    );
    if (!program?.organizationPaymentConfigurationId) return null;
    const fleet =
      fleetsByPaymentConfig[program.organizationPaymentConfigurationId];
    if (!fleet?.city_ids?.length) return null;
    return fleet.city_ids[0];
  };

  // Backend already filters programs and claims by whitelabel, so no frontend filtering needed
  const filteredRewardClaims = useMemo(() => {
    if (!rewardClaims || !rewardClaims.content) return [];
    return rewardClaims.content;
  }, [rewardClaims]);

  const filteredRewardPrograms = useMemo(() => {
    if (!rewardPrograms || !rewardPrograms.content) return [];
    return rewardPrograms.content;
  }, [rewardPrograms]);

  const columns = useMemo(
    () => [
      {
        name: "Name",
        selector: "name",
        sortable: true,
        center: true,
      },
      {
        name: "Fleet",
        selector: "organizationPaymentConfigurationId",
        sortable: false,
        center: true,
        cell: (row) => {
          if (fleetsLoading) {
            return <CircularProgress size={16} />;
          }
          return getFleetName(row.organizationPaymentConfigurationId);
        },
      },
      {
        name: "Amount",
        selector: "amount",
        sortable: false,
        center: true,
        cell: (row) => {
          if (
            Object.keys(rulesByProgramId).length === 0 &&
            rewardPrograms?.content?.length > 0
          ) {
            return <CircularProgress size={16} />;
          }
          return getAmountDisplay(
            row.id,
            row.organizationPaymentConfigurationId
          );
        },
      },
      {
        name: "Priority",
        selector: "priority",
        sortable: false,
        center: true,
        cell: (row) => {
          if (
            Object.keys(rulesByProgramId).length === 0 &&
            rewardPrograms?.content?.length > 0
          ) {
            return <CircularProgress size={16} />;
          }
          return getPriorityDisplay(row.id);
        },
      },
      {
        name: "Status",
        selector: "isActive",
        sortable: true,
        center: true,
        cell: (row) => (
          <Tag
            text={row.isActive ? "Active" : "Inactive"}
            colorTag={row.isActive ? TAG_COLOR.GREEN : TAG_COLOR.RED}
          />
        ),
      },
      {
        name: "Start Date",
        selector: "startDate",
        sortable: true,
        center: true,
        cell: (row) => (row?.startDate ? dateFormat(row.startDate) : "-"),
      },
      {
        name: "End Date",
        selector: "endDate",
        sortable: true,
        center: true,
        cell: (row) => (row?.endDate ? dateFormat(row.endDate) : "-"),
      },
      {
        name: "Actions",
        center: true,
        cell: (row) => (
          <>
            <Tooltip title="View/Manage Rules" placement="top">
              <IconButton
                color="primary"
                onClick={() => {
                  navigate(`/dashboard/promotions/rewards/${row.id}`);
                }}
              >
                <VisibilityIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Edit Program" placement="top">
              <IconButton
                color="primary"
                onClick={() => {
                  setSelectedProgram(row);
                  setIsEdit(true);
                  setModalVisible(true);
                }}
              >
                <EditIcon />
              </IconButton>
            </Tooltip>
          </>
        ),
      },
    ],
    [
      fleetsLoading,
      getFleetName,
      fleetCurrencies,
      rulesByProgramId,
      rewardPrograms,
      navigate,
    ]
  );

  const claimsColumns = useMemo(
    () => [
      {
        name: "User",
        selector: "legacyUserId",
        sortable: false,
        center: true,
        cell: (row) => {
          const legacyId = extractLegacyUserId(row);
          const isUserLoading = legacyId && !usersMap.has(String(legacyId));
          if (isUserLoading) {
            return <CircularProgress size={16} />;
          }
          const displayName = getUserDisplay(legacyId);
          const cityId = getCityIdForClaim(row);
          return legacyId && cityId ? (
            <Link
              to={`/dashboard/customer/${legacyId}/${cityId}`}
              target="_blank"
              style={{ color: "#1976d2", textDecoration: "none" }}
            >
              {displayName}
            </Link>
          ) : legacyId ? (
            <span>{displayName}</span>
          ) : (
            <Tooltip title={row.userId} placement="top">
              <span>{truncateUuid(row.userId)}</span>
            </Tooltip>
          );
        },
      },
      {
        name: "Rule Name",
        selector: "rewardRuleId",
        sortable: false,
        center: true,
        cell: (row) => {
          if (!rewardRules?.content) {
            return <CircularProgress size={16} />;
          }
          return getRuleName(row.rewardRuleId);
        },
      },
      {
        name: "Amount",
        selector: "rewardAmountMinor",
        sortable: true,
        center: true,
        cell: (row) => formatClaimAmount(row.rewardAmountMinor, row.currency),
      },
      {
        name: "Status",
        selector: "status",
        sortable: true,
        center: true,
        cell: (row) => (
          <Tag text={row.status} colorTag={getStatusColor(row.status)} />
        ),
      },
      {
        name: "Claimed At",
        selector: "claimedAt",
        sortable: true,
        center: true,
        cell: (row) => dateFormat(row.claimedAt),
      },
    ],
    [rewardRules, usersMap, fleetsByPaymentConfig, rewardPrograms]
  );

  const claimsFilterObject = [
    {
      filterBy: "status",
      filterValue: ["PENDING", "COMPLETED", "FAILED", "REVERSED", "EXPIRED"],
    },
  ];

  const addButtonClick = () => {
    setSelectedProgram(null);
    setIsEdit(false);
    setModalVisible(true);
  };

  const handleDownloadCsv = () => {
    setStartDownload(true);
    setTimeout(() => {
      setStartDownload(false);
    }, 500);
  };

  const csvHeaders = [
    { label: "Claim ID", key: "id" },
    { label: "User ID", key: "userId" },
    { label: "Rule ID", key: "rewardRuleId" },
    { label: "Amount (Minor)", key: "rewardAmountMinor" },
    { label: "Currency", key: "currency" },
    { label: "Status", key: "status" },
    { label: "Claimed At", key: "claimedAt" },
  ];

  const {
    renderCustomTable,
    renderHeaderBar,
    localPage,
    localRows,
    sortData,
    debouncedSearchTerm,
    filterData,
    setLoading,
    resetPagination,
    searchFilter,
  } = useCustomTable(
    columns,
    { ...rewardPrograms, content: filteredRewardPrograms },
    null,
    addButtonClick,
    [],
    "Rewards",
    true,
    true
  );

  const {
    renderCustomTable: renderClaimsTable,
    renderHeaderBar: renderClaimsHeaderBar,
    localPage: claimsLocalPage,
    localRows: claimsLocalRows,
    filterData: claimsFilterData,
    setLoading: setClaimsLoading,
  } = useCustomTable(
    claimsColumns,
    { ...rewardClaims, content: filteredRewardClaims },
    null,
    null,
    claimsFilterObject,
    "Reward Claims",
    true,
    false
  );

  const reloadData = useCallback(() => {
    setLoading(true);
    dispatch(
      getRewardPrograms({
        page: localPage,
        size: localRows,
      })
    );
  }, [dispatch, localPage, localRows, setLoading]);

  const reloadClaimsData = useCallback(() => {
    setClaimsLoading(true);
    const filterPayload = {
      page: claimsLocalPage,
      size: claimsLocalRows,
    };

    if (claimsFilterData && claimsFilterData.length > 0) {
      claimsFilterData.forEach((filter) => {
        if (filter.filterBy === "status") {
          filterPayload.status = filter.filter;
        }
      });
    }

    dispatch(getRewardClaims(filterPayload));
  }, [
    dispatch,
    claimsLocalPage,
    claimsLocalRows,
    claimsFilterData,
    setClaimsLoading,
  ]);

  useEffect(() => {
    if (rewardProgramsSuccess) {
      setLoading(false);
    }
  }, [rewardProgramsSuccess]);

  useEffect(() => {
    if (rewardClaimsSuccess) {
      setClaimsLoading(false);
    }
  }, [rewardClaimsSuccess]);

  useEffect(() => {
    reloadData();
  }, [reloadData]);

  useEffect(() => {
    reloadClaimsData();
  }, [reloadClaimsData]);

  const dateFormat = (date) => Moment(date).format("DD.MM.YYYY HH:mm");

  const handleClose = () => {
    setModalVisible(false);
    setIsEdit(false);
    setSelectedProgram(null);
  };

  useEffect(() => {
    if (createSuccess === true) {
      showToast("Signup Bonus Created", "success");
      reloadData();
      dispatch(clearRewardProgram());
      setModalVisible(false);
    }
  }, [createSuccess, reloadData, dispatch, showToast]);

  useEffect(() => {
    if (updateSuccess === true) {
      showToast("Signup Bonus Updated", "success");
      reloadData();
      dispatch(clearRewardProgram());
      setModalVisible(false);
    }
  }, [updateSuccess, reloadData, dispatch, showToast]);

  return (
    <>
      <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
        <Tabs
          value={activeTab}
          onChange={(e, newValue) => setActiveTab(newValue)}
        >
          <Tab label="Programs" />
          <Tab label="Claims" />
        </Tabs>
      </Box>

      {activeTab === 0 && (
        <>
          {renderHeaderBar()}
          {renderCustomTable()}
        </>
      )}

      {activeTab === 1 && (
        <>
          <Box
            sx={{
              padding: "10px 0",
              display: "flex",
              flexDirection: "row",
              justifyContent: "flex-end",
            }}
          >
            <Tooltip title="Download Claims CSV">
              <IconButton size="small" onClick={handleDownloadCsv}>
                <GetAppIcon />
              </IconButton>
            </Tooltip>
          </Box>

          {startDownload && (
            <CSVDownload
              data={filteredRewardClaims}
              headers={csvHeaders}
              target="_blank"
              filename="reward-claims.csv"
            />
          )}

          {renderClaimsHeaderBar()}
          {renderClaimsTable()}
        </>
      )}

      <RewardProgramModal
        visible={modalVisible}
        onClose={handleClose}
        isEdit={isEdit}
        programData={selectedProgram}
      />
    </>
  );
};

export default RewardsContainer;
